import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Radar, Target, Home, ArrowRight, Clock } from "lucide-react";
import api from "@/lib/api";

const TOOLS = [
    {
        id: "acquisizione",
        icon: Search,
        name: "Acquisizione Privati",
        status: { label: "On demand", cls: "bg-slate-100 text-slate-500 border-slate-200" },
        copy: "Mentre aspetti che un privato ti chiami, un'altra agenzia ha già bussato alla sua porta. Disegna la tua zona sulla mappa e scova in autonomia i proprietari che vendono senza agenzia.",
        cta: "Apri Acquisizione Privati",
        rows: [
            { title: "Attico con Terrazzo", meta: "Selargius", badge: "Privato" },
            { title: "Villa con Giardino", meta: "Sassari", badge: "Privato" },
            { title: "Appartamento Vista Mare", meta: "Alghero", badge: "Privato" },
        ],
        badgeCls: "bg-amber-100 text-amber-700",
    },
    {
        id: "nuovi-privati",
        icon: Radar,
        name: "Nuovi Privati",
        status: { label: "Tempo reale", cls: "bg-rose-50 text-rose-600 border-rose-200", pulse: true },
        copy: "I privati pubblicano l'annuncio e in poche ore arrivano già dieci telefonate di agenzia. Ti avvisiamo noi, appena l'annuncio nasce, con il match automatico sui tuoi acquirenti già in CRM.",
        cta: "Apri Nuovi Privati",
        rows: [
            { title: "Trilocale Ristrutturato", meta: "Bologna · 9 ore fa", badge: "Nuovo" },
            { title: "Appartamento Luminoso", meta: "Sassari · 15 ore fa", badge: "Nuovo" },
            { title: "Bilocale Centro Storico", meta: "Cagliari · 15 ore fa", badge: "Nuovo" },
        ],
        badgeCls: "bg-blue-100 text-blue-700",
    },
    {
        id: "next-seller",
        icon: Target,
        name: "Next Seller",
        status: { label: "In prova · 30g", cls: "bg-blue-50 text-blue-600 border-blue-200" },
        copy: "I venditori più preziosi sono quelli che nessuno ha ancora chiamato: pignoramenti pre-asta, successioni, dismissioni aziendali. Next Seller legge i segnali pubblici e ti avvisa prima che il mercato se ne accorga.",
        cta: "Apri Next Seller",
        rows: [
            { score: 60, title: "Beni immobiliari", meta: "Regolamento condominiale", badge: "Segnale" },
            { score: 55, title: "Immobile · Successione", meta: "Esecuzione immobiliare", badge: "Segnale" },
            { score: 55, title: "Immobile · Successione", meta: "Integrazione patrimoniale", badge: "Segnale" },
        ],
        badgeCls: "bg-amber-100 text-amber-700",
    },
];

const backendUrl = process.env.REACT_APP_BACKEND_URL;

function ToolRow({ row }) {
    return (
        <div data-testid="agency-tool-mock-row" className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-slate-50">
            {row.score != null ? (
                <span className="shrink-0 w-9 h-9 rounded-lg bg-slate-200 text-slate-700 text-xs font-bold flex items-center justify-center">{row.score}</span>
            ) : (
                <span className="shrink-0 w-9 h-9 rounded-lg bg-slate-200 text-slate-400 flex items-center justify-center"><Home size={15} /></span>
            )}
            <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-slate-800 truncate">{row.title}</div>
                <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">{row.meta?.includes("fa") && <Clock size={10} />} {row.meta}</div>
            </div>
        </div>
    );
}

function ToolCard({ tool }) {
    const Icon = tool.icon;
    return (
        <Link
            to="/prezzi"
            data-testid={`agency-tool-card-${tool.id}`}
            className="news-card-neu group flex flex-col p-6 md:p-7 transition-transform duration-300"
        >
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-slate-100 text-blue-700 flex items-center justify-center shrink-0"><Icon size={19} /></span>
                    <h3 className="font-serif text-lg leading-tight">{tool.name}</h3>
                </div>
                <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${tool.status.cls}`}>
                    {tool.status.pulse && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />}
                    {tool.status.label}
                </span>
            </div>
            <p className="text-sm leading-relaxed mb-5">{tool.copy}</p>
            <div className="space-y-2 mb-6">
                {tool.rows.map((row, i) => (
                    <div key={i} className="relative">
                        <ToolRow row={row} />
                        <span className={`absolute top-2 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full ${tool.badgeCls}`}>{row.badge}</span>
                    </div>
                ))}
            </div>
            <div data-testid={`agency-tool-cta-${tool.id}`} className="mt-auto w-full rounded-full border border-slate-200 text-slate-700 text-sm font-semibold py-3 flex items-center justify-center gap-2 group-hover:border-blue-400 group-hover:text-blue-700 transition-colors">
                {tool.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </div>
        </Link>
    );
}

export function AgencyToolsShowcase() {
    const [partnerLogos, setPartnerLogos] = useState([]);
    useEffect(() => { api.get("/public/partner-logos").then(({ data }) => setPartnerLogos(data.items || [])).catch(() => setPartnerLogos([])); }, []);

    return (
        <section data-testid="agency-tools-section" className="w-full px-6 md:px-12 lg:px-20 2xl:px-28 py-24 lg:py-32 border-t border-white/10">
            <div className="max-w-3xl">
                <span className="label-eyebrow">Per le agenzie immobiliari</span>
                <h2 className="font-serif text-4xl sm:text-5xl text-ink-50 mt-3 leading-tight">
                    Ogni giorno <span className="italic text-gold">perdi incarichi</span> che non sapevi nemmeno di poter acquisire.
                </h2>
                <p className="text-ink-400 mt-6 leading-relaxed text-base md:text-lg">
                    Gli incarichi non aspettano. E nemmeno i tuoi concorrenti. Ogni giorno nuovi proprietari decidono di vendere casa. VendiDirect ti aiuta ad arrivare prima degli altri, intercettando venditori e opportunità immobiliari per anticipare il mercato con 3 strumenti pensati per le agenzie.
                </p>
            </div>

            <div data-testid="agency-tools-grid" className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-14">
                {TOOLS.map((tool) => <ToolCard key={tool.id} tool={tool} />)}
            </div>

            {partnerLogos.length > 0 && (
                <div data-testid="agency-logos-row" className="mt-20 pt-14 border-t border-white/10 text-center">
                    <span className="label-eyebrow">Già scelto da queste agenzie</span>
                    <div className="flex flex-wrap items-center justify-center gap-8 md:gap-10 mt-7 opacity-80">
                        {partnerLogos.map((item) => (
                            <img key={item.id} data-testid={`agency-logo-${item.id}`} src={`${backendUrl}${item.image_url}`} alt={`Logo ${item.name}`} loading="lazy" decoding="async" className="h-9 md:h-10 w-auto object-contain" />
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-16 text-center">
                <Link to="/prezzi" data-testid="agency-tools-final-cta" className="cta-gold cta-gold-pulse inline-flex px-10 py-4 text-sm">
                    Inizia gratis
                </Link>
            </div>
        </section>
    );
}
